"use client";

import { useEffect, useRef } from 'react';

import {
  createMathJaxTimer,
  trackFormulaRendering,
} from '../utils/newrelic-mathjax';

interface MathRendererProps {
  block?: boolean;
  children: string;
  className?: string;
}

const MathRenderer: React.FC<MathRendererProps> = ({
  block = false,
  children,
  className = '',
}) => {
  const divRef = useRef<HTMLDivElement>(null);
  const spanRef = useRef<HTMLSpanElement>(null);

  const processText = (text: string): string => {
    // Distinguish between currency and math expressions
    const hasDisplayMath = /\$\$[^$]+\$\$/.test(text);

    // Match expressions like $20\sqrt{2}$, $\frac{a}{b}$, $x^2$, etc.
    const hasInlineMath = /\$[^$]*[a-zA-Z\\^_{}()[\]|><~:=+\-*/][^$]*\$/.test(
      text
    );

    const hasCurrency = /-?\$\d+(\.\d{2})?(?!\$)/.test(text);

    if (hasCurrency && !hasDisplayMath && !hasInlineMath) {
      return text;
    }

    if (hasDisplayMath || hasInlineMath) {
      return text;
    }

    const processedText = text
      .replace(/∠/g, '\\angle ')
      .replace(/≅/g, '\\cong ')
      .replace(/~/g, '\\sim ')
      .replace(/△/g, '\\triangle ')
      // Handle complex degree expressions first
      .replace(
        /(\d+)°\s*([+\-×÷=])\s*(\d+)°\s*([+\-×÷=])\s*(\d+)°\s*([+\-×÷=])\s*(\d+)°/g,
        '$1^\\circ $2 $3^\\circ $4 $5^\\circ $6 $7^\\circ'
      )
      .replace(
        /(\d+)°\s*([+\-×÷=])\s*(\d+)°\s*([+\-×÷=])\s*(\d+)°/g,
        '$1^\\circ $2 $3^\\circ $4 $5^\\circ'
      )
      .replace(/(\d+)°\s*([+\-×÷=])\s*(\d+)°/g, '$1^\\circ $2 $3^\\circ')
      .replace(/(\d+)°/g, '$1^\\circ');

    // Only wrap in math delimiters if the text actually contains LaTeX commands
    if (processedText.includes('\\')) {
      return block ? `$$${processedText}$$` : `$${processedText}$`;
    }

    return text;
  };

  useEffect(() => {
    const renderMath = async () => {
      const currentRef = block ? divRef.current : spanRef.current;

      if (window.MathJax && currentRef) {
        const timer = createMathJaxTimer();
        const componentType = block ? 'block-math' : 'inline-math';

        try {
          if (window.MathJax.startup?.promise) {
            await window.MathJax.startup.promise;
          }

          if (window.MathJax.typesetPromise) {
            await window.MathJax.typesetPromise([currentRef]);
          }

          const renderTime = timer.end();
          trackFormulaRendering.success(1, renderTime, componentType);

          trackFormulaRendering.slowRendering(renderTime, 1000, 1);
        } catch (error) {
          console.error('MathJax rendering error:', error);
          trackFormulaRendering.failure(
            error as Error,
            children,
            componentType
          );
        }
      }
    };

    const handleMathJaxReady = () => {
      renderMath();
    };

    const handleRerenderNeeded = () => {
      if (window.MathJax?.startup?.promise) {
        window.MathJax.startup.promise.then(() => {
          renderMath();
        });
      } else {
        renderMath();
      }
    };

    if (window.MathJax?.startup?.promise) {
      window.MathJax.startup.promise.then(() => {
        renderMath();
      });
    } else if (window.__MATHJAX_READY__) {
      renderMath();
    }

    window.addEventListener('mathjax-ready', handleMathJaxReady);
    window.addEventListener('mathjax-rerender-needed', handleRerenderNeeded);

    return () => {
      window.removeEventListener('mathjax-ready', handleMathJaxReady);
      window.removeEventListener(
        'mathjax-rerender-needed',
        handleRerenderNeeded
      );
    };
  }, [children, block]);

  const processedText = processText(children);

  if (block) {
    return (
      <div
        className={`math-content ${className}`}
        dangerouslySetInnerHTML={{ __html: processedText }}
        ref={divRef}
      />
    );
  }

  return (
    <span
      className={`math-content ${className}`}
      dangerouslySetInnerHTML={{ __html: processedText }}
      ref={spanRef}
      style={{ display: 'inline' }}
    />
  );
};

export default MathRenderer;
